import { CdkOverlayOrigin, OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { environment } from 'src/environments/environment';
import { FilterListMenuOutletComponent } from './filter-list-menu-outlet/filter-list-menu-outlet.component';
import { CapabilityCheckPipe, canAccessNotificationCenter, canOrderEquipment } from '../../pipes/check-capability.pipe';
import { HrCapabilityCheckPipe } from '../../pipes/hr-capability-check.pipe';
import { JwtService } from 'src/app/services/jwt.service';
import { storageKey } from 'src/app/shared/utils/storage.util';
import { UserMeasuresService } from 'src/app/services/user-measures.service';
import { StartProcessService } from '../visits-reports/start/start-process.service';
import { ApiResponses, BackendService } from 'src/app/services/backend.service';
import { HrElevationService } from 'src/app/services/hr-elevation.service';
import { HrFeaturesService } from 'src/app/services/hr-features.service';
type StaticPage = ApiResponses['static/getAllPages'][0];

/** The submenus that fly out to the right of the side menu. */
type Flyout = 'personnel' | 'administration';

/** Grace period that lets the pointer travel from the trigger into the flyout. */
const FLYOUT_CLOSE_DELAY_MS = 150;

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  providers: [StartProcessService],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatListModule,
    MatIconModule,
    OverlayModule,
    RouterModule,
    FilterListMenuOutletComponent,
    TranslocoModule,
    CapabilityCheckPipe,
    HrCapabilityCheckPipe,
    RouterModule
  ]
})
export class NavigationComponent implements OnDestroy {
  @Input() staticPageMenu: { [s: string]: StaticPage[] } = {};
  @Output() readonly itemSelected = new EventEmitter();

  currentDate = new Date();
  title = environment.appTitle;
  production = environment.production;
  version = localStorage.getItem(storageKey('app_version'));

  openFlyout: Flyout | null = null;
  protected readonly hrFeatures = inject(HrFeaturesService);

  /**
   * Flush against the side menu's right edge, so the pointer can cross over
   * without a gap. Falls back to the left for a menu docked on the right.
   */
  readonly flyoutPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' }
  ];

  private closeTimer?: ReturnType<typeof setTimeout>;

  get roleId() {
    return this.jwtService.role?.id;
  }

  get canOrderEquipment(): boolean {
    return canOrderEquipment(this.jwtService.role?.capabilities, this.userMeasuresService.equipmentClassId);
  }

  get canAccessNotifications(): boolean {
    return canAccessNotificationCenter(this.jwtService.role?.capabilities);
  }

  /** The personnel section is an HR admin surface; self-service lives under /my. */
  get canAccessPersonnel(): boolean {
    return this.hrElevationService.hasAnyCapability('hr.admin.');
  }

  /** The administration flyout is empty without at least one of the screens it holds. */
  get canAccessAdministration(): boolean {
    return (
      this.hrElevationService.hasCapability('hr.admin.personnel.read') ||
      this.hrElevationService.hasCapability('hr.admin.options') ||
      this.hrElevationService.hasCapability('hr.admin.users') ||
      this.hrElevationService.hasCapability('hr.admin.roles') ||
      this.hrElevationService.hasCapability('hr.admin.planning.write')
    );
  }

  constructor(
    private readonly jwtService: JwtService,
    private readonly userMeasuresService: UserMeasuresService,
    private readonly startProcessService: StartProcessService,
    private readonly backendService: BackendService,
    private readonly router: Router,
    private readonly hrElevationService: HrElevationService
  ) {
    console.log(this.title);

  }

  ngOnDestroy(): void {
    clearTimeout(this.closeTimer);
  }

  showFlyout(flyout: Flyout): void {
    this.keepFlyout();
    this.openFlyout = flyout;
  }

  hideFlyout(): void {
    this.keepFlyout();
    this.closeTimer = setTimeout(() => (this.openFlyout = null), FLYOUT_CLOSE_DELAY_MS);
  }

  keepFlyout(): void {
    clearTimeout(this.closeTimer);
  }

  closeFlyout(): void {
    this.keepFlyout();
    this.openFlyout = null;
  }

  /**
   * The overlay reports a click on the trigger as an outside click, and the
   * trigger's own handler toggles right after - closing here would reopen it.
   */
  closeOnOutsideClick(event: MouseEvent, trigger: CdkOverlayOrigin): void {
    if (trigger.elementRef.nativeElement.contains(event.target as Node)) return;

    this.closeFlyout();
  }

  /** Touch devices never hover, so tapping the trigger has to open the flyout too. */
  toggleFlyout(flyout: Flyout): void {
    if (this.openFlyout === flyout) return this.closeFlyout();

    this.showFlyout(flyout);
  }

  selectItem(): void {
    this.closeFlyout();
    this.itemSelected.emit();
  }

  handleStaticPageAction(staticPage: any): void {
    switch (staticPage.action?.action) {
      case 'startProcessByDefinitionId':
        this.backendService
          .post<'processEngineProcess/getAllActiveSteps'>('process-engine/process/getAllActiveSteps', {
            body: {
              onlyInputSteps: true,
              includeProcessDataFields: true,
              criteria: {
                '$process.process_definition_id$': staticPage.action?.processDefinitionId,
                '$process.createdByUserId$': this.jwtService.userData.userId,
                executed: null
              }
            } as any // TODO: type
          })
          .subscribe(async (steps) => {
            this.startProcessService.startProcess(
              staticPage.action?.processDefinitionId,
              null,
              null,
              'emergency',
              steps.result as any, // TODO: type
              await this.startProcessService.generatePredefinedDatafieldsByDefinitionId(16),
              true
            );
          });
        break;
      case 'navigateTo':
        let path = staticPage.action?.path;
        const queryParams = staticPage.action?.queryParams;

        try {
          const parsed = JSON.parse(path);
          path = Array.isArray(parsed) ? parsed : [path];
        } catch {
          path = [path];
        }
        if (path.find((p) => p.includes('visits-reports'))) {
          this.router.navigate(path, { queryParams });
        } else {
          this.router.navigate([...path, staticPage.staticPageId], { queryParams });
        }
        break;
    }
  }
}
